// Paddle Billing v2 wrapper. The <script src="cdn.paddle.com/paddle/v2/paddle.js"> tag
// in index.html exposes a global `Paddle` object once loaded; we wait for it lazily so
// the rest of the app does not block on Paddle's CDN.

declare global {
  interface Window {
    Paddle?: PaddleGlobal;
  }
}

interface PaddleGlobal {
  Environment: { set: (env: 'sandbox' | 'production') => void };
  Initialize: (opts: { token: string; eventCallback?: (event: unknown) => void }) => void;
  Checkout: {
    open: (opts: PaddleCheckoutOpts) => void;
  };
}

export interface PaddleCheckoutOpts {
  items: Array<{ priceId: string; quantity: number }>;
  customer?: { email?: string };
  customData?: Record<string, string>;
  settings?: {
    displayMode?: 'overlay' | 'inline';
    successUrl?: string;
    theme?: 'light' | 'dark';
  };
}

const PADDLE_TOKEN: string | undefined = import.meta.env.VITE_PADDLE_CLIENT_TOKEN;
const PADDLE_ENV: string | undefined = import.meta.env.VITE_PADDLE_ENVIRONMENT;

let initialized = false;

async function waitForPaddle(timeoutMs = 8000): Promise<PaddleGlobal> {
  const start = Date.now();
  while (!window.Paddle) {
    if (Date.now() - start > timeoutMs) {
      throw new Error('Paddle SDK failed to load — check that the <script> tag is present');
    }
    await new Promise(r => setTimeout(r, 50));
  }
  return window.Paddle;
}

async function ensurePaddleInitialized(): Promise<PaddleGlobal> {
  const paddle = await waitForPaddle();
  if (initialized) return paddle;

  if (!PADDLE_TOKEN) {
    throw new Error(
      'VITE_PADDLE_CLIENT_TOKEN is not configured — set it in .env.local or .env.prod',
    );
  }

  if (PADDLE_ENV === 'sandbox') {
    paddle.Environment.set('sandbox');
  }
  paddle.Initialize({ token: PADDLE_TOKEN });
  initialized = true;
  return paddle;
}

export interface OpenCheckoutParams {
  paddlePriceId: string;
  organizationId: string;
  customerEmail?: string;
  successUrl?: string;
}

function assertPaddlePriceId(value: string): void {
  if (/^pri_[a-z0-9]+$/.test(value)) return;

  throw new Error(
    'Invalid Paddle price id. Update the credit product to use a real Paddle price id (pri_...) from the Paddle dashboard.',
  );
}

export async function openPaddleCheckout(params: OpenCheckoutParams): Promise<void> {
  assertPaddlePriceId(params.paddlePriceId);

  const paddle = await ensurePaddleInitialized();
  paddle.Checkout.open({
    items: [{ priceId: params.paddlePriceId, quantity: 1 }],
    customer: params.customerEmail ? { email: params.customerEmail } : undefined,
    // CRITICAL: backend extracts organization_id from data.custom_data.organization_id in
    // the Paddle webhook. Forgetting this means the purchase is parked unprocessed and
    // requires manual reconciliation.
    customData: { organization_id: params.organizationId },
    settings: {
      displayMode: 'overlay',
      successUrl:
        params.successUrl ?? `${window.location.origin}/billing?status=success`,
    },
  });
}

export function isPaddleConfigured(): boolean {
  return !!PADDLE_TOKEN;
}
